import type { CombatMission } from "./combatMissions";
import type { HaulingMission } from "./haulingMissions";
import type { MiningMission } from "./miningMissions";
import type { AssignedCombatMission, AssignedMiningMission } from "./missionBoardAssembly";
import type { VipTransportMission } from "./vipTransportMissions";

// Mission Board Filters -- LOCKED (design doc, unnumbered subsection
// between 16.26 and 16.27): four filter buttons, Combat/Mining/Hauling/VIP
// Transport. Every generated mission carries one of these four type tags;
// filtering is a pure client-side view concern, not a generation-time
// restriction -- the board still generates a mixed pool underneath, filters
// just control what's currently displayed.
//
// Per 16.33, a board is a live function of (current system, ship) -- every
// mission carries the system it's actually assigned to (systemId): for
// Hauling/VIP that's just their own originSystemId; for Combat/Mining it's
// whichever nearby system missionBoardAssembly.ts assigned them to.
export type MissionType = "Combat" | "Mining" | "Hauling" | "VIP Transport";

export type BoardMission =
  | { type: "Combat"; rewardCr: number; systemId: string; combat: CombatMission }
  | { type: "Mining"; rewardCr: number; systemId: string; mining: MiningMission }
  | { type: "Hauling"; rewardCr: number; systemId: string; hauling: HaulingMission }
  | { type: "VIP Transport"; rewardCr: number; systemId: string; vip: VipTransportMission };

export function tagCombatMissions(assigned: AssignedCombatMission[]): BoardMission[] {
  return assigned.map(({ mission, systemId }) => ({
    type: "Combat",
    rewardCr: mission.rewardCr,
    systemId,
    combat: mission
  }));
}

export function tagMiningMissions(assigned: AssignedMiningMission[]): BoardMission[] {
  return assigned.map(({ mission, systemId }) => ({
    type: "Mining",
    rewardCr: mission.rewardCr,
    systemId,
    mining: mission
  }));
}

export function tagHaulingMissions(missions: HaulingMission[]): BoardMission[] {
  return missions.map((hauling) => ({
    type: "Hauling",
    rewardCr: hauling.rewardCr,
    systemId: hauling.originSystemId,
    hauling
  }));
}

// generateVipTransportMission() returns null when a ship can't fit even 1
// VIP at a tier's footprint (vipTransportMissions.ts's generation gate) --
// filtered out here rather than tagged, so a gated-out mission never
// reaches the board at all.
export function tagVipTransportMissions(
  missions: (VipTransportMission | null)[]
): BoardMission[] {
  return missions
    .filter((vip): vip is VipTransportMission => vip !== null)
    .map((vip) => ({
      type: "VIP Transport",
      rewardCr: vip.rewardCr,
      systemId: vip.originSystemId,
      vip
    }));
}

// Combines already-generated, already-tagged mission arrays into one board.
// Each mission type's own generator (combatMissions.ts, miningMissions.ts,
// haulingMissions.ts) stays independent -- this is purely the aggregation
// layer, matching the doc's "generates a mixed pool underneath" framing.
export function combineMissionBoard(...missionGroups: BoardMission[][]): BoardMission[] {
  return missionGroups.flat();
}

// Pure client-side view filter (the four buttons) -- never restricts what
// was generated, only what's currently shown.
export function filterMissionsByType(board: BoardMission[], type: MissionType): BoardMission[] {
  return board.filter((m) => m.type === type);
}
